import { AfterViewInit, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';

import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { tinykeys } from 'tinykeys';

import { SHARED_MODULES } from '../../../shared.';
import { PhTooltipDirective } from '../../directives/ph-tooltip.directive';
import { HotkeyTooltipComponent } from '../hotkey-tooltip/hotkey-tooltip.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';

export interface ZoomParams {
	min: number,
	max: number,
	step: number,
	value: number
}

const defaultZoom: ZoomParams = {
	min: 20,
	max: 300,
	step: 20,
	value: 100
}

@Component({
    selector: 'app-zoom',
    templateUrl: './zoom.component.html',
    styleUrls: ['./zoom.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [SHARED_MODULES, PhTooltipDirective, HotkeyTooltipComponent, PhIconWrapperComponent],
	standalone: true
})
export class ZoomComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

    zoom: FormControl<number> = new FormControl<number>(defaultZoom.value);

	@Input() set target(el: HTMLElement | Array<HTMLElement> | null) {
		const newElements: HTMLElement[] = Array.isArray(el) ? el.filter(e => e != null) : el ? [el] : [];

		if (this._initialized) {
			this.updateListeners(this._targetElements, newElements);
		}

		this._targetElements = newElements;
	}

    @Input() min: number = defaultZoom.min;
    @Input() max: number = defaultZoom.max;
    @Input() step: number = defaultZoom.step;
    @Input() disabled: boolean = false;
    @Input() available: boolean = true;

	@Input('zoom') set zoomValue(value: number) {
		if (this.zoom.value !== value) {
			this.zoom.setValue(value);
		}
	}

	@Output('apply') changeEmitter: EventEmitter<number> = new EventEmitter<number>();

    private _targetElements: Array<HTMLElement> = [];
    private _initialized: boolean = false;
    private wheelListener: (e: WheelEvent) => void;
    private tinyKeysSubscription: () => void;

    constructor(private elementRef: ElementRef) {}

	ngOnInit(): void {
		this.zoom.valueChanges
			.pipe(debounceTime(500))
			.subscribe((value: number): void => {
				this.changeEmitter.emit(value)
			});

		this.tinyKeysSubscription = tinykeys(window, {
			'$mod+Equal': (event: KeyboardEvent): void => {
				if (this.available) {
					event.preventDefault();
					this.zoomIncrease();
				}
			},
            '$mod+NumpadAdd': (event: KeyboardEvent): void => {
				if (this.available) {
					event.preventDefault();
					this.zoomIncrease();
				}
			},
            '$mod+Minus': (event: KeyboardEvent): void => {
				if (this.available) {
					event.preventDefault();
					this.zoomDecrease();
				}
			},
			'$mod+NumpadSubtract': (event: KeyboardEvent): void => {
				if (this.available) {
					event.preventDefault();
					this.zoomDecrease();
				}
			}
		});

        this.wheelListener = this.onWheel.bind(this);
    }

    ngAfterViewInit(): void {
		if (this._targetElements.length === 0) {
			this._targetElements = [this.elementRef.nativeElement.parentElement];
		}

		this._targetElements.forEach((element: HTMLElement) => {
			element.addEventListener('wheel', this.wheelListener, { passive: false });
		});
		
		this._initialized = true;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this._initialized && changes['target']) {
			const previousElements: Array<HTMLElement> = changes['target'].previousValue
				? (Array.isArray(changes['target'].previousValue) 
					? changes['target'].previousValue.filter((element: HTMLElement) => element != null)
					: [changes['target'].previousValue])
				: [];
			this.updateListeners(previousElements, this._targetElements);
		}
	}

	private updateListeners(oldElements: Array<HTMLElement>, newElements: Array<HTMLElement>): void {
		// Remove listeners from old elements
		oldElements.forEach((element: HTMLElement) => {
			if (element && !newElements.includes(element)) {
				element.removeEventListener('wheel', this.wheelListener);
			}
		});

		// Add listeners to new elements
		newElements.forEach((element: HTMLElement) => {
			if (element && !oldElements.includes(element)) {
				element.addEventListener('wheel', this.wheelListener, { passive: false });
			}
		});
	}

	zoomDecrease(): void {
		if (this.disabled) return;
		const value: number = Math.max(this.zoom.value - this.step, this.min);
		if (value !== this.zoom.value) this.zoom.setValue(value);
	}

	zoomIncrease(): void {
		if (this.disabled) return;
		const value: number = Math.min(this.zoom.value + this.step, this.max);
		if (value !== this.zoom.value) this.zoom.setValue(value);
	}

	private onWheel(event: WheelEvent): void {
		if ((event.ctrlKey || event.metaKey) && event.deltaY !== 0) {
			event.preventDefault();

			if (event.deltaY > 0) {
				this.zoomDecrease();
			} else {
				this.zoomIncrease();
			}
		}
	}

	ngOnDestroy(): void {
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();
		this._targetElements.forEach((element: HTMLElement) => element.removeEventListener('wheel', this.wheelListener));
	}
}
